import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "../components/Loading";

/**
 * A list the server answers, narrowed by a filter the user types.
 *
 * RULE, all four parts of it at once:
 *  1. one request per typed word, not per letter — the text is held until the typing stops;
 *  2. the rows already on screen stay while a newer answer is on its way, so the skeleton is
 *     shown only when there is nothing at all to show;
 *  3. an answer a newer request has superseded is thrown away, never written to the screen;
 *  4. a failed request ends the wait.
 *
 * Break any one of them and the screen fails in a way nobody reports as a bug. Clear the rows on
 * every change of the filter and the skeleton blinks under the typing instead of a list. Write
 * whichever answer lands last and the unfiltered list can land on top of the filtered one, because
 * the network does not promise to answer two requests in the order they were asked. Leave the wait
 * running when a request fails and the skeleton turns forever with nothing left to arrive.
 *
 * The delay is not cosmetic: one answer of the receivables screen is six statements over the whole
 * ledger and takes about 1,4 s against the full data set, so a word typed letter by letter would
 * otherwise put seven of them in the air at once, each one slower than the last.
 */

/** How long the typed text is held before a request goes out, in milliseconds. */
const ODLAGANJE = 400;

export type FilteredQuery<T> = {
  /** The answer on screen. Null only until the first one arrives. */
  data: T | null;
  /** Message of the last failed request; cleared as soon as a new one goes out. */
  error: string | null;
  /**
   * Nothing to show yet and a request is out: the one case where a screen draws a skeleton.
   * It never returns to true once an answer has landed.
   */
  skeleton: boolean;
  /** Rows are on screen and a newer answer is on its way: a quiet marker, never a skeleton. */
  refreshing: boolean;
  /** Ask the same question again — after a write that changed the answer. */
  reload: () => void;
  /** For a row a screen changes on its own and does not want to re-read the whole page for. */
  setData: Dispatch<SetStateAction<T | null>>;
};

export function useFilteredQuery<T>(
  uzmi: (text: string) => Promise<T>,
  {
    keys,
    text = "",
    delay = ODLAGANJE,
    onError,
  }: {
    /**
     * Everything other than the text that decides the answer: page, sort, chosen filters. Values
     * must be primitives, because a change is recognised by comparing them as text.
     */
    keys: readonly unknown[];
    /** The typed filter. Its changes wait out `delay`; everything in `keys` goes out at once. */
    text?: string;
    delay?: number;
    /** For a screen that reports a failure as a toast rather than in the page. */
    onError?: (greska: unknown) => void;
  },
): FilteredQuery<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [radi, setRadi] = useState(true);
  const [ponovo, setPonovo] = useState(0);

  // The text the requests actually go out with: it follows the typed one a pause later, which is
  // what turns a typed word into one request. Clearing the field is answered at once — there is
  // nothing left to wait for, and a cleared filter is the moment the user wants the whole list
  // back.
  const [smireno, setSmireno] = useState(text);
  useEffect(() => {
    if (smireno === text) return;
    if (text === "") {
      setSmireno("");
      return;
    }
    const id = setTimeout(() => setSmireno(text), delay);
    return () => clearTimeout(id);
  }, [text, smireno, delay]);

  // The request and the failure handler are read out of a box, not out of the effect's own
  // closure: both are rebuilt on every render, and depending on them would send a request on every
  // render instead of on a change of the filter.
  const uzmiRef = useRef(uzmi);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    uzmiRef.current = uzmi;
    onErrorRef.current = onError;
  });

  const kljuc = JSON.stringify([keys, smireno, ponovo]);

  // Every request gets a number and only the newest number may write to the screen. Two are in
  // the air whenever a filter changes while an answer is still on its way, and the older one may
  // well be the one that lands last.
  const izdanje = useRef(0);

  useEffect(() => {
    const moje = (izdanje.current += 1);
    let vazi = true;
    setRadi(true);
    setError(null);
    uzmiRef.current(smireno)
      .then((vrednost) => {
        if (!vazi || moje !== izdanje.current) return;
        setData(vrednost);
        setRadi(false);
      })
      .catch((greska: unknown) => {
        if (!vazi || moje !== izdanje.current) return;
        setError(poruka(greska));
        onErrorRef.current?.(greska);
        // The wait ends with the failure. Keeping it on is what leaves a skeleton turning over a
        // question that has already been answered, with an error.
        setRadi(false);
      });
    return () => {
      vazi = false;
    };
    // `smireno` and everything in `keys` are already inside `kljuc`; adding them would only
    // re-send the same request on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kljuc]);

  const reload = useCallback(() => setPonovo((n) => n + 1), []);

  return {
    data,
    error,
    skeleton: data === null && radi,
    refreshing: data !== null && radi,
    reload,
    setData,
  };
}

/** What an error is called on screen; an API failure carries a message, anything else is shown raw. */
function poruka(greska: unknown): string {
  if (greska && typeof greska === "object" && "message" in greska) {
    const m = (greska as { message?: unknown }).message;
    if (typeof m === "string" && m.trim() !== "") return m;
  }
  return String(greska);
}

/**
 * The marker that says a newer answer is on its way, for the screens that keep their rows while
 * they wait. Small and beside the filter on purpose: a spinner over the table would hide exactly
 * the rows this whole hook exists to keep.
 */
export function Refreshing({ on }: { on: boolean }) {
  const { t } = useTranslation();
  // Hooks first, the decision after. A return above a hook changes the hook order between two
  // renders, and React answers that by tearing the screen down.
  if (!on) return null;
  return (
    <span
      role="status"
      aria-live="polite"
      style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--ink-3)", fontSize: 12 }}
    >
      <Spinner size={12} />
      {t("common.refreshing", "osvežava se")}
    </span>
  );
}
